import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Coins, Gem, Flame, Gift, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';

interface DailyLoginBonusProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  streakDay: number;
  currentStreak: number;
  longestStreak: number;
  reward: { coins: number; gems: number };
  onClaim: () => void;
  claiming: boolean;
}

export const DailyLoginBonus = ({
  open,
  onOpenChange,
  streakDay,
  currentStreak,
  longestStreak,
  reward,
  onClaim,
  claiming,
}: DailyLoginBonusProps) => {
  const getMultiplier = (day: number) => {
    const multipliers = [1, 1.2, 1.4, 1.6, 1.8, 2.0, 2.5];
    return multipliers[(day - 1) % 7];
  };

  const multiplier = getMultiplier(streakDay);
  const isDay7 = (streakDay % 7) === 0 || streakDay === 7;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl flex items-center justify-center gap-2">
            <Gift className="w-6 h-6 text-primary" />
            Daily Login Bonus!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Streak Info */}
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Day {streakDay} of 7</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="font-semibold">{currentStreak} Day Streak</span>
            </div>
          </div>

          {/* Reward Display */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className={`relative rounded-xl p-8 text-center ${
              isDay7
                ? 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/50'
                : 'bg-gradient-to-br from-primary/10 to-secondary/10 border-2 border-primary/30'
            }`}
          >
            {isDay7 && (
              <div className="absolute top-2 right-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
              </div>
            )}
            
            <div className="mb-3">
              <p className="text-sm font-medium text-muted-foreground mb-4">
                {isDay7 ? "🎉 WEEK COMPLETE!" : "TODAY'S REWARD"}
              </p>
              
              <div className="flex items-center justify-center gap-4">
                <motion.div
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex items-center gap-2"
                >
                  <Coins className="w-8 h-8 text-yellow-500" />
                  <span className="text-4xl font-bold">{reward.coins}</span>
                </motion.div>

                {reward.gems > 0 && (
                  <>
                    <span className="text-2xl text-muted-foreground">+</span>
                    <motion.div
                      animate={{ rotate: [0, -10, 10, -10, 0] }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      className="flex items-center gap-2"
                    >
                      <Gem className="w-8 h-8 text-cyan-500" />
                      <span className="text-4xl font-bold">{reward.gems}</span>
                    </motion.div>
                  </>
                )}
              </div>

              {multiplier > 1 && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-sm font-semibold text-primary mt-3"
                >
                  {multiplier}x Streak Bonus!
                </motion.p>
              )}
            </div>
          </motion.div>

          {/* Week Progress */}
          <div>
            <p className="text-sm font-medium text-center mb-3 text-muted-foreground">
              Week Progress
            </p>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                const isCompleted = day < streakDay;
                const isCurrent = day === streakDay;
                const isDay7Indicator = day === 7;

                return (
                  <div
                    key={day}
                    className={`relative flex flex-col items-center gap-1`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                        isCompleted
                          ? 'bg-primary text-primary-foreground'
                          : isCurrent
                          ? 'bg-primary/30 text-primary border-2 border-primary ring-2 ring-primary/20'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isCompleted ? '✓' : day}
                    </div>
                    {isDay7Indicator && (
                      <Gem className="w-3 h-3 text-cyan-500 absolute -bottom-4" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-center text-muted-foreground mt-5">
              Day 7 Bonus: 62 coins + 3 gems! 💎
            </p>
          </div>

          {/* Longest Streak Info */}
          {longestStreak > currentStreak && (
            <div className="text-center text-sm text-muted-foreground">
              <Trophy className="w-4 h-4 inline-block mr-1" />
              Your longest streak: {longestStreak} days
            </div>
          )}

          {/* Claim Button */}
          <Button
            onClick={onClaim}
            disabled={claiming}
            size="lg"
            className="w-full text-lg font-semibold"
          >
            {claiming ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2" />
                Claiming...
              </>
            ) : (
              <>
                <Gift className="w-5 h-5 mr-2" />
                Claim Reward
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
